/**
 * Wastage EOM — the words on the page.
 *
 * Every FIGURE arrives settled from /api/reports/wastage-eom (backend/routes/wastageEomReport.js
 * owns the buckets, the cost ladder, the markup and the quadrant split). This module only
 * arranges those figures the way the reference report reads: which source is largest, which
 * items lost the most profit, what share of the month a category is, and the plain-language
 * sentences around them. Nothing here re-costs a row or re-decides a bucket.
 *
 * Pure functions. No React, no DOM.
 */

export const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (n) => Number(n || 0);
const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** "a", "a and b", "a, b and c" — then "and 3 more" when the list was cut short. */
export const listNames = (names, more = 0) => {
  const n = (names || []).filter(Boolean);
  if (more > 0) return `${n.join(', ')} and ${more} more`;
  if (n.length <= 1) return n[0] || '';
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
};

/** A share as the reference prints it: whole percent, never a bare "0%" for a real amount. */
export const sharePct = (part, whole) => {
  if (!(whole > 0) || !(part > 0)) return 0;
  return Math.round((part / whole) * 100);
};
const pctText = (part, whole) => {
  const p = sharePct(part, whole);
  return p === 0 && part > 0 ? 'under 1%' : `${p}%`;
};

const items = (part) => (part && Array.isArray(part.items) ? part.items : []);
const totalOf = (part) => (part && part.total) || { cost: 0, lost_revenue: 0, lost_gp: 0 };

// ---------------------------------------------------------------------------
// 01 — the four sources of loss
// ---------------------------------------------------------------------------
/**
 * The four buckets the server keeps apart, each with the raw cost the server put on it.
 * The pattern belongs to the SOURCE, not to its rank, so floor wastage reads the same on
 * every month's report however the order changes.
 */
const SOURCES = [
  { key: 'floorWastage', label: 'Floor Wastage', pattern: 0, phrase: 'operational floor wastage' },
  { key: 'spoilage', label: 'Spoilage / Expiry', pattern: 1, phrase: 'preventable spoilage' },
  { key: 'ncCapitalLoss', label: 'NC Packaging', pattern: 3, phrase: 'packaging damage' },
  { key: 'prepError', label: 'Prep / Batch', pattern: 2, phrase: 'batch / prep loss' },
];

export function sourceBars(ledger) {
  return SOURCES.map((s, order) => ({ ...s, order, value: num(ledger && ledger[s.key] && ledger[s.key].cost) }))
    .sort((a, b) => b.value - a.value || a.order - b.order);
}

export function ownerRead(summary, ledger) {
  const ranked = sourceBars(ledger).filter((s) => s.value > 0);
  if (ranked.length === 0) {
    return 'Nothing was recorded as wasted this month — no food, packaging or batch prep went into the bin.';
  }
  const [a, b] = ranked;
  const lead = b
    ? `${capitalise(a.phrase)} (${money(a.value)}) and ${b.phrase} (${money(b.value)}) are the two largest sources.`
    : `${capitalise(a.phrase)} (${money(a.value)}) is the only source of loss this month.`;
  const food = num(summary.raw_material_cost);
  const tail = food > 0
    ? ` In total the kitchen threw away ${money(food)} of food at purchase cost, and gave up ${money(
      summary.cumulative_lost_gross_profit
    )} of gross profit that would otherwise have reached the business.`
    : ' No food was thrown away — the whole loss was packaging, at purchase cost.';
  return lead + tail;
}

// ---------------------------------------------------------------------------
// 02 — quadrant matrix
// ---------------------------------------------------------------------------
/** Describes the split the SERVER made (median per unit family × median lost GP). */
export function quadrantLede(quadrants) {
  const q = quadrants || {};
  const count = ['q1', 'q2', 'q3', 'q4'].reduce((n, k) => n + (Array.isArray(q[k]) ? q[k].length : 0), 0);
  const medGp = q.medians ? num(q.medians.gross_profit) : 0;
  const gpRule = medGp > 0
    ? `high profit loss is at or above the median lost gross profit of ${money(medGp)}. An item that lost no profit is never a high profit loss.`
    : 'high profit loss is any item that lost gross profit at all — most of this month’s wasted items lost none, so the median is ₹0.00.';
  return `Every wasted food item (${count} this month) is placed on two questions — how much of it was thrown away, compared only with items measured in the same unit, and how much gross profit it cost. High volume is at or above the median quantity for its unit; ${gpRule}`;
}

// ---------------------------------------------------------------------------
// Part 1 — master food wastage (floor wastage + expired stock, as the reference lists them)
// ---------------------------------------------------------------------------
/**
 * The reference lists expired stock beside the floor wastage, each with its own reason. The
 * server keeps them in two buckets (part1_master, part3_expiry) because expired stock carries
 * no lost sale; that rule is kept — expired rows show ₹0 lost sales and ₹0 lost profit — and
 * the two server totals are added for the TOTAL row.
 */
export function masterLedger(data) {
  const master = items(data.part1_master).map((it) => ({ ...it, expired: false }));
  const expired = items(data.part3_expiry).map((it) => ({ ...it, expired: true }));
  const rows = [...master, ...expired].sort((a, b) => num(b.cost) - num(a.cost));
  const m = totalOf(data.part1_master);
  const x = totalOf(data.part3_expiry);
  return {
    rows,
    expiredCount: expired.length,
    total: {
      cost: num(m.cost) + num(x.cost),
      lost_revenue: num(m.lost_revenue) + num(x.lost_revenue),
      lost_gp: num(m.lost_gp) + num(x.lost_gp),
    },
  };
}

/** How the server values a lost sale (wastageEomReport.js: menu price, else cost × markup). */
export function lostSalesNote(summary, hasExpired) {
  const markup = num(summary.markup_used);
  const basis = summary.markup_is_default
    ? `no food cost was recorded on this month's sales, so a standard ×${markup.toFixed(2)} is used`
    : `this month's sales brought in ₹${markup.toFixed(2)} for every ₹1 of food cost`;
  return `How lost sales are worked out: a wasted dish is valued at its menu price; a wasted ingredient or batch prep is valued at its cost times this month's markup — ${basis}. Lost Gross Profit = Lost Sales − Ingredient Cost.${
    hasExpired ? ' Expired or spoiled stock carries neither: it went bad before it could be cooked or sold, so its loss is the purchase cost alone.' : ''
  }`;
}

// ---------------------------------------------------------------------------
// 03 — category ledger
// ---------------------------------------------------------------------------
const CATEGORIES = [
  { key: 'floorWastage', label: 'Floor Wastage (Raw)', name: 'Floor Wastage' },
  { key: 'prepError', label: 'Preparation / Batch', name: 'Preparation / Batch' },
  { key: 'spoilage', label: 'Spoilage / Expiry', name: 'Spoilage / Expiry' },
  { key: 'ncCapitalLoss', label: 'NC Capital Loss', name: 'NC Capital Loss' },
];

/** Status = the category's share of the month's total waste cost (food + packaging). */
export const statusFor = (share) => (share >= 40 ? 'Critical' : share >= 15 ? 'Watch' : 'Low');

export function categoryRows(ledger) {
  const rows = CATEGORIES.map((c) => {
    const l = (ledger && ledger[c.key]) || {};
    return { ...c, cost: num(l.cost), revenue: num(l.revenue), profit: num(l.profit) };
  });
  const total = rows.reduce((s, r) => s + r.cost, 0);
  return {
    total,
    rows: rows.map((r) => {
      const share = total > 0 ? (r.cost / total) * 100 : 0;
      return { ...r, share, status: total > 0 ? statusFor(share) : '—' };
    }),
  };
}

export function categoryReading(ledger) {
  const { rows, total } = categoryRows(ledger);
  if (!(total > 0)) return 'Nothing was recorded as wasted this month, so there is no category to compare.';
  const largest = [...rows].sort((a, b) => b.cost - a.cost)[0];
  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
  let text = `${largest.name} is the largest category at ${money(largest.cost)} — ${pctText(largest.cost, total)} of everything wasted this month`;
  text += largest.profit > 0
    ? ` — and it cost ${money(largest.profit)} in lost gross profit.`
    : '. It had no sale to lose, so its loss is the purchase cost alone.';
  if (largest.key !== 'ncCapitalLoss' && byKey.ncCapitalLoss.cost > 0) {
    text += ` Packaging has no selling price, so its loss is the purchase cost alone (${money(byKey.ncCapitalLoss.cost)}).`;
  }
  if (largest.key !== 'spoilage' && byKey.spoilage.cost > 0) {
    text += ` Expired stock has no grey bar for the same reason: it went bad before it could be sold (${money(byKey.spoilage.cost)}).`;
  }
  return `${text} Where the grey bar is much longer than the dark bar, the food was worth far more on a plate than it cost to buy — waste there hurts profit the most.`;
}

// ---------------------------------------------------------------------------
// 04 — next month waste controls
// ---------------------------------------------------------------------------
const ADVICE = [
  [/burn|over.?cook|under.?cook|charr/, 'Watch the cooking time and temperature for this item, especially during busy hours, and make sure the recipe card is followed.'],
  [/expir|spoil|stale|rott|mould|mold|fung|sour/, 'Order smaller quantities, check dates and condition when stock arrives, and always use the oldest stock first.'],
  [/fat|trim|clean|bone|skin|peel/, 'Check how this item is cleaned and trimmed. Weigh the trimmings for one week; if they stay high, retrain the staff doing it or ask the supplier for better-cleaned cuts.'],
  [/over.?prep|over.?produc|excess|left.?over|unsold|extra/, 'Prepare closer to what actually sells — set the daily prep quantity from the last few weeks of sales.'],
  [/hold|shelf.?life|time exceed/, 'Make smaller batches, closer to the time they are needed, and label every batch with the time it was made.'],
  [/drop|spill|floor|broke|fell|fall/, 'Check how this item is carried and stored at the pass — use trays, keep the route clear at peak service, and store it where it cannot fall.'],
  [/return|complain|reject|customer/, 'Find out why customers sent it back — taste, temperature or waiting time — and check the recipe card is followed every time.'],
  [/wrong|mistake|error/, 'Check how orders reach the kitchen — read each ticket back before cooking so the wrong dish is not made.'],
];
const DEFAULT_ADVICE =
  'Find out why this item keeps being thrown away — go through its wastage entries with the staff who made them, and agree one fix for next month.';

export const adviceFor = (reason) => {
  const r = String(reason || '').toLowerCase();
  const hit = ADVICE.find(([re]) => re.test(r));
  return hit ? hit[1] : DEFAULT_ADVICE;
};

const happened = (it) =>
  `${it.qty_display || 'Some'} was thrown away${it.reason ? ` (${it.reason})` : ''}. It cost ${money(it.cost)} to buy and could have sold for ${money(
    it.lost_revenue
  )}, so ${money(it.lost_gp)} of profit was lost.`;

const mainly = (list) => {
  const names = list.slice(0, 3).map((it) => it.item_name);
  return listNames(names, Math.max(list.length - 3, 0));
};

export function buildControls(data) {
  const summary = data.summary || {};
  const losers = [...items(data.part1_master), ...items(data.part4_prep)]
    .filter((it) => num(it.lost_gp) > 0)
    .sort((a, b) => num(b.lost_gp) - num(a.lost_gp));
  const boxGp = num(summary.cumulative_lost_gross_profit);

  let oneLine;
  if (losers.length > 0) {
    const top = losers.slice(0, 3);
    const protect = top.reduce((s, it) => s + num(it.lost_gp), 0);
    const names = listNames(top.map((it) => it.item_name));
    oneLine = top.length === 1
      ? `${names} caused ${pctText(protect, boxGp)} of this month's lost profit. Fixing this one item alone would protect about ${money(protect)} next month.`
      : `${names} caused ${pctText(protect, boxGp)} of this month's lost profit. Fixing these ${top.length} items alone would protect about ${money(protect)} next month.`;
  } else if (num(summary.raw_material_cost) > 0 || num(summary.nc_capital_loss) > 0) {
    oneLine = `No wasted item lost a sale this month — the whole loss was purchase cost: ${money(summary.raw_material_cost)} of food and ${money(
      summary.nc_capital_loss
    )} of packaging.`;
  } else {
    oneLine = 'Nothing was recorded as wasted this month. Keep logging every discard so next month’s report can show where the money goes.';
  }

  const blocks = [];
  if (losers[0]) {
    blocks.push({ key: 'first', title: `Biggest loss this month: ${losers[0].item_name}`, happened: happened(losers[0]), todo: adviceFor(losers[0].reason) });
  }
  if (losers[1]) {
    blocks.push({ key: 'second', title: `Second biggest loss: ${losers[1].item_name}`, happened: happened(losers[1]), todo: adviceFor(losers[1].reason) });
  }

  const expired = items(data.part3_expiry);
  const expiredCost = num(totalOf(data.part3_expiry).cost);
  if (expired.length > 0 && expiredCost > 0) {
    blocks.push({
      key: 'expiry',
      title: `${money(expiredCost)} of stock went bad before it was used`,
      happened: expired.length === 1
        ? `${expired[0].item_name} spoiled or expired before it could be used.`
        : `${expired.length} items spoiled or expired, mainly ${mainly(expired)}.`,
      todo: 'Order these in smaller quantities, check dates and condition when stock arrives, and always use the oldest stock first.',
    });
  }

  const prep = items(data.part4_prep);
  const prepCost = num(totalOf(data.part4_prep).cost);
  if (prep.length > 0 && prepCost > 0) {
    blocks.push({
      key: 'prep',
      title: `Batch preparations worth ${money(prepCost)} were thrown away`,
      happened: prep.length === 1
        ? `${prep[0].item_name} was made in advance but not used in time.`
        : `Mainly ${mainly(prep)}, made in advance but not used in time.`,
      todo: 'Make smaller batches, closer to the time they are needed, and label every batch with the time it was made.',
    });
  }

  const nc = items(data.part2_nc);
  const ncCost = num(totalOf(data.part2_nc).cost);
  if (nc.length > 0 && ncCost > 0) {
    blocks.push({
      key: 'nc',
      title: `Packaging worth ${money(ncCost)} was damaged or discarded`,
      happened: nc.length === 1 ? `${nc[0].item_name} was damaged or discarded.` : `Mainly ${mainly(nc)}.`,
      todo: 'Store packaging dry and off the floor, and check the sealing equipment so fewer containers are spoiled.',
    });
  }

  const zero = num(summary.zero_cost_rows);
  if (zero > 0) {
    blocks.push({
      key: 'zero-cost',
      title: `${zero} wastage ${zero === 1 ? 'entry has' : 'entries have'} no cost on record`,
      happened: `${zero === 1 ? 'It has' : 'They have'} no usable recipe or batch cost, so ${zero === 1 ? 'it counts' : 'they count'} as ₹0.00 in every figure above.`,
      todo: `Fix the recipe or batch cost for ${zero === 1 ? 'this item' : 'these items'} so next month’s figures are complete.`,
    });
  }

  return { oneLine, blocks };
}
